import React, { Fragment, useState, useEffect } from "react";
import { connect } from "react-redux";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import AppButton from "../../Common/Button/Button";
import {
  convertCapabilityName,
  isJsonParseSuccessful,
  isJsonStringifySuccessful,
  validateJSONData,
} from "../../../utils/general";
import Grid2 from "@mui/material/Grid2";
import FormInput from "../../Common/FormInputs/MuiTextField";
import { selectMenuProps } from "../../../utils/selectMenuProps";

const selectInputSx = {
  height: 45,
  minHeight: 45,
  width: "100%",
  margin: 0,
  "& .MuiOutlinedInput-root": {
    height: 45,
    minHeight: 45,
    margin: 0,
  },
  "& .MuiSelect-select": {
    height: "45px !important",
    minHeight: "45px !important",
    display: "flex",
    alignItems: "center",
    boxSizing: "border-box",
    paddingTop: "0 !important",
    paddingBottom: "0 !important",
  },
  "& .MuiOutlinedInput-notchedOutline": { border: "1px solid rgba(0, 0, 0, 0.42) !important" },
};
const addCapabilityButtonSx = {
  height: 45,
  minHeight: 45,
  boxSizing: "border-box",
};
const capabilityCardSx = { marginBottom: "15px", backgroundColor: "#f2f2f2" };

const CONFIG = {
  customJson: {
    label: "Custom Capability JSON",
    placeholder: 'e.g., {"capability": "groupCommand","attributes": [{"key": "groupCommand","minFwVersion": 2.05}]}',
    rows: 4,
    helpText: "Valid JSON required",
  },
};

const getConfigCapabilitiesMap = (deviceModelsMetadata, productCategory) => {
  const config = deviceModelsMetadata?.categories?.[productCategory]?.configCapabilities;
  if (!config) return {};
  // Same metadata shape as diag/status: { GroupCommand: { capabilityName: "groupCommand" } }
  if (!Array.isArray(config) && typeof config === "object") {
    return config;
  }
  if (Array.isArray(config)) {
    return config.reduce((acc, item) => {
      const name = item?.capability || item?.capabilityName;
      if (name) {
        acc[name] = item;
      }
      return acc;
    }, {});
  }
  return {};
};

const buildAttributeState = (metadataAttribute = {}, savedAttribute = null) => {
  const hasMinFwVersion = Object.prototype.hasOwnProperty.call(metadataAttribute || {}, "minFwVersion")
    || savedAttribute?.minFwVersion != null;
  const minFwVersion = savedAttribute?.minFwVersion ?? metadataAttribute?.minFwVersion;
  const state = {
    key: savedAttribute?.key ?? metadataAttribute?.key ?? "",
  };

  if (hasMinFwVersion) {
    state.minFwVersion = minFwVersion != null ? String(minFwVersion) : "";
  }

  return state;
};

const getCapabilityValue = (configMap, metadataKey) =>
  configMap?.[metadataKey]?.capabilityName || metadataKey;

// AC metadata lists GroupCommand with capabilityName only. The saved attribute
// still needs key + minFwVersion. Use metadata attributes when they exist.
const getResolvedAttributes = (metadataEntry) => {
  if (Array.isArray(metadataEntry?.attributes) && metadataEntry.attributes.length > 0) {
    return metadataEntry.attributes;
  }
  const capabilityValue = metadataEntry?.capabilityName;
  if (!capabilityValue) return [];
  const attribute = { key: capabilityValue };
  if (String(capabilityValue).toLowerCase() === "groupcommand") {
    attribute.minFwVersion = 2.05;
  }
  return [attribute];
};

const resolveMetadataKey = (savedCapability, configMap) => {
  if (!savedCapability) return "";
  if (configMap[savedCapability]) return savedCapability;
  const matchedKey = Object.keys(configMap).find(
    (key) => configMap[key]?.capabilityName === savedCapability
  );
  return matchedKey || "";
};

const DeviceModelConfigCapability = ({
  productCategory,
  isDeviceModelAddedUsingFileUpload,
  triggerAddNewDeviceModel,
  addDeviceConfigCapabilities,
  editDeviceModel,
  deviceModelData,
  ModelData: { deviceModelsMetadata },
  areAllCustomJsonValidCheck,
}) => {
  const customCapabilityName = "CustomCapability";
  const [customCapabilityCount, setCustomCapabilityCount] = useState(1);
  const [configCapabilitiesList, setConfigCapabilitiesList] = useState([]);
  const [selectedConfigCapability, setSelectedConfigCapability] = useState("");
  const [addedConfigCapabilities, setAddedConfigCapabilities] = useState([]);
  const [configCapabilitiesAttributes, setConfigCapabilitiesAttributes] = useState({});
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const areAllCustomJsonValid = (errorsObj) => Object.values(errorsObj).every((error) => error === "");
  const isCustomCapability = (value) => typeof value === "string" && value.startsWith(customCapabilityName);
  const isKnownCapability = (value) =>
    Boolean(getConfigCapabilitiesMap(deviceModelsMetadata, productCategory)?.[value]);
  const isCustomLike = (value) => isCustomCapability(value) || !isKnownCapability(value);

  useEffect(() => {
    if (configCapabilitiesList.length > 0) {
      setSelectedConfigCapability(configCapabilitiesList[0]);
    } else {
      setSelectedConfigCapability("");
    }
  }, [configCapabilitiesList]);

  useEffect(() => {
    const configMap = getConfigCapabilitiesMap(deviceModelsMetadata, productCategory);
    const attrState = {};

    if (editDeviceModel || isDeviceModelAddedUsingFileUpload) {
      const savedConfigCapabilities = Array.isArray(deviceModelData?.configCapabilities)
        ? deviceModelData.configCapabilities
        : [];
      savedConfigCapabilities.forEach((capability) => {
        const metadataKey = resolveMetadataKey(capability?.capability, configMap);
        if (!metadataKey) {
          if (capability?.capability) {
            attrState[capability.capability] = isJsonStringifySuccessful(capability, { formatted: true });
          }
          return;
        }
        const metadataAttributes = getResolvedAttributes(configMap[metadataKey]);
        const savedAttributes = Array.isArray(capability?.attributes) ? capability.attributes : [];
        attrState[metadataKey] = { attributes: {} };
        const attributeCount = Math.max(
          Array.isArray(metadataAttributes) ? metadataAttributes.length : 0,
          savedAttributes.length,
        );
        for (let index = 0; index < attributeCount; index += 1) {
          attrState[metadataKey].attributes[index] = buildAttributeState(
            metadataAttributes?.[index],
            savedAttributes[index],
          );
        }
      });
    }

    Object.keys(configMap).forEach((metadataKey) => {
      if (attrState[metadataKey]) return;
      const metadataAttributes = getResolvedAttributes(configMap[metadataKey]);
      attrState[metadataKey] = { attributes: {} };
      if (!metadataAttributes.length) return;
      metadataAttributes.forEach((attribute, index) => {
        attrState[metadataKey].attributes[index] = buildAttributeState(attribute);
      });
    });
    setConfigCapabilitiesAttributes(attrState);
  }, [productCategory, editDeviceModel, isDeviceModelAddedUsingFileUpload, deviceModelsMetadata, deviceModelData]);

  useEffect(() => {
    const configMap = getConfigCapabilitiesMap(deviceModelsMetadata, productCategory);
    const capabilities = Object.keys(configMap).sort();

    if (editDeviceModel || isDeviceModelAddedUsingFileUpload) {
      const savedItems = Array.isArray(deviceModelData?.configCapabilities)
        ? deviceModelData.configCapabilities
        : [];
      const savedNames = savedItems
        .map((item) => resolveMetadataKey(item?.capability, configMap) || item?.capability)
        .filter(Boolean);
      const savedOrdered = [
        ...savedNames.filter((cap) => !isCustomLike(cap)),
        ...savedNames.filter((cap) => isCustomLike(cap)),
      ];
      setAddedConfigCapabilities(savedOrdered);
      setConfigCapabilitiesList([
        customCapabilityName,
        ...capabilities.filter((cap) => cap !== customCapabilityName && !savedNames.includes(cap)),
      ]);
    } else {
      setConfigCapabilitiesList([
        customCapabilityName,
        ...capabilities.filter((cap) => cap !== customCapabilityName),
      ]);
      setAddedConfigCapabilities([]);
    }
  }, [productCategory, editDeviceModel, isDeviceModelAddedUsingFileUpload, deviceModelsMetadata, deviceModelData]);

  useEffect(() => {
    if (!triggerAddNewDeviceModel) return;
    addDeviceConfigCapabilities(getConfigCapabilityData());
    areAllCustomJsonValidCheck(areAllCustomJsonValid(errors), "config");
  }, [triggerAddNewDeviceModel]);

  function handleAddConfigCapability(addedCapabilityName) {
    if (!addedCapabilityName) return;
    if (addedCapabilityName === customCapabilityName) {
      const nextName = `${customCapabilityName}${customCapabilityCount}`;
      setConfigCapabilitiesAttributes((prev) => ({
        ...prev,
        [nextName]: isJsonStringifySuccessful({}, { formatted: true }),
      }));
      setAddedConfigCapabilities((prevState) => {
        const customs = prevState.filter((cap) => isCustomCapability(cap));
        const nonCustoms = prevState.filter((cap) => !isCustomCapability(cap));
        return [...customs, nextName, ...nonCustoms];
      });
      setCustomCapabilityCount((prev) => prev + 1);
      return;
    }

    setAddedConfigCapabilities((prevState) => [...prevState, addedCapabilityName]);
    setConfigCapabilitiesList((prevState) =>
      prevState.filter((capabilityName) => capabilityName !== addedCapabilityName),
    );
  }

  function handleRemoveConfigCapability(removedCapabilityName) {
    if (isCustomLike(removedCapabilityName)) {
      setConfigCapabilitiesAttributes((prev) => {
        const { [removedCapabilityName]: _, ...rest } = prev;
        return rest;
      });
      setAddedConfigCapabilities((prevState) =>
        prevState.filter((capabilityName) => capabilityName !== removedCapabilityName),
      );
      setErrors((prev) => {
        const { [removedCapabilityName]: _, ...rest } = prev;
        return rest;
      });
      setTouched((prev) => {
        const { [removedCapabilityName]: _, ...rest } = prev;
        return rest;
      });
      return;
    }

    setAddedConfigCapabilities((prevState) =>
      prevState.filter((capabilityName) => capabilityName !== removedCapabilityName),
    );
    setConfigCapabilitiesList((prevState) => [...prevState, removedCapabilityName]);
  }

  function onChangeCapabilityCustomJson(capabilityName, value) {
    setConfigCapabilitiesAttributes((prev) => ({ ...prev, [capabilityName]: value }));
    const error = validateJSONData(value);
    setErrors((prev) => ({ ...prev, [capabilityName]: error }));
    if (error && error !== "") {
      setTouched((prev) => ({ ...prev, [capabilityName]: "true" }));
    }
  }

  function onChangeMinFwVersion(capabilityName, attributeIndex, value) {
    setConfigCapabilitiesAttributes((prev) => ({
      ...prev,
      [capabilityName]: {
        ...prev[capabilityName],
        attributes: {
          ...prev[capabilityName]?.attributes,
          [attributeIndex]: {
            ...prev[capabilityName]?.attributes?.[attributeIndex],
            minFwVersion: value,
          },
        },
      },
    }));
  }

  function getConfigCapabilityData() {
    const configCapabilities = [];
    const configMap = getConfigCapabilitiesMap(deviceModelsMetadata, productCategory);

    for (const capabilityName of addedConfigCapabilities) {
      if (isCustomCapability(capabilityName) || !configMap[capabilityName]) {
        const parsed = isJsonParseSuccessful(configCapabilitiesAttributes[capabilityName], { logError: false });
        if (parsed !== false) {
          configCapabilities.push(parsed);
        }
        continue;
      }

      const capabilityData = {
        capability: getCapabilityValue(configMap, capabilityName),
      };
      const metadataAttributes = getResolvedAttributes(configMap[capabilityName]);
      const stateAttributes = configCapabilitiesAttributes?.[capabilityName]?.attributes;

      if (stateAttributes && Object.keys(stateAttributes).length) {
        capabilityData.attributes = [];
        for (const index of Object.keys(stateAttributes)) {
          const metadataAttribute = metadataAttributes?.[index] || {};
          const current = stateAttributes[index] || {};
          const attributeData = {
            key: current.key ?? metadataAttribute.key ?? "",
          };

          if (Object.prototype.hasOwnProperty.call(current, "minFwVersion")) {
            const parsedMinFwVersion = Number(current.minFwVersion);
            if (
              current.minFwVersion === "" ||
              current.minFwVersion === null ||
              current.minFwVersion === undefined ||
              Number.isNaN(parsedMinFwVersion) ||
              parsedMinFwVersion < 0
            ) {
              return { error: "Min FW Version is required for Config Capabilities" };
            }
            attributeData.minFwVersion = parsedMinFwVersion;
          }

          capabilityData.attributes.push(attributeData);
        }
      }

      configCapabilities.push(capabilityData);
    }

    return configCapabilities;
  }

  return (
    <Fragment>
      <Grid2 container spacing={2} sx={{ alignItems: "flex-end", mt: 1.5, mb: 2 }}>
        <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
          <FormInput
            type="select"
            label="Select Capability"
            value={selectedConfigCapability}
            onChange={(e) => setSelectedConfigCapability(e.target.value)}
            options={(configCapabilitiesList ?? []).map((capabilityName) => ({
              value: capabilityName,
              label: convertCapabilityName(capabilityName),
            }))}
            fullWidth
            size="small"
            sx={selectInputSx}
            MenuProps={selectMenuProps}
            inputProps={{ "aria-label": "Select capability" }}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: "auto" }} sx={{ display: "flex", alignItems: "flex-end" }}>
          <AppButton
            variant="primary"
            size="medium"
            onClick={() => handleAddConfigCapability(selectedConfigCapability)}
            disabled={!configCapabilitiesList || configCapabilitiesList.length === 0}
            sx={addCapabilityButtonSx}
          >
            Add Capability
          </AppButton>
        </Grid2>
      </Grid2>
      {addedConfigCapabilities.map((capabilityName) => {
        const isCustom = isCustomLike(capabilityName);
        const metadataAttributes = getResolvedAttributes(
          getConfigCapabilitiesMap(deviceModelsMetadata, productCategory)?.[capabilityName],
        );
        return (
          <Fragment key={`${productCategory}_${capabilityName}`}>
            <Card variant="outlined" sx={capabilityCardSx}>
              <CardContent>
                {isCustom ? (
                  <Grid2 container alignItems="flex-start" spacing={1}>
                    <Grid2 size={10} sx={{ margin: "auto" }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {CONFIG.customJson.label}
                      </Typography>
                    </Grid2>
                    <Grid2 size={2} sx={{ display: "flex", justifyContent: "flex-end" }}>
                      <IconButton
                        aria-label="delete"
                        onClick={() => handleRemoveConfigCapability(capabilityName)}
                        sx={{ p: 0.5 }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Grid2>
                    <Grid2 size={12}>
                      <FormInput
                        id={capabilityName}
                        name={capabilityName}
                        type="textarea"
                        multiline
                        rows={CONFIG.customJson.rows}
                        fullWidth
                        value={
                          typeof configCapabilitiesAttributes[capabilityName] === "string"
                            ? configCapabilitiesAttributes[capabilityName]
                            : ""
                        }
                        onChange={(e) => onChangeCapabilityCustomJson(capabilityName, e.target.value)}
                        placeholder={CONFIG.customJson.placeholder}
                        error={!!errors?.[capabilityName]}
                        touched={touched[capabilityName]}
                        helperText={errors?.[capabilityName] || CONFIG.customJson.helpText}
                        sx={{
                          width: "100%",
                          minWidth: "100%",
                          "& .MuiOutlinedInput-input": {
                            padding: "8.5px 14px !important",
                            height: "130px !important",
                          },
                          "& .MuiOutlinedInput-root": {
                            minHeight: 130,
                            height: "auto",
                            alignItems: "flex-start",
                            "& .MuiOutlinedInput-notchedOutline": {
                              border: "1px solid rgba(0, 0, 0, 0.42) !important",
                            },
                          },
                        }}
                        slotProps={{
                          htmlInput: {
                            maxLength: 10000,
                          },
                        }}
                      />
                    </Grid2>
                  </Grid2>
                ) : (
                  <Grid2 container spacing={2} sx={{ alignItems: "flex-end" }}>
                    <Grid2 size={{ xs: 10, sm: 11 }}>
                      <Typography variant="button" sx={{ fontWeight: 600 }}>
                        {convertCapabilityName(capabilityName)}
                      </Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 2, sm: 1 }} sx={{ display: "flex", justifyContent: "flex-end" }}>
                      <IconButton
                        aria-label="delete"
                        onClick={() => handleRemoveConfigCapability(capabilityName)}
                        sx={{ p: 0.5 }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Grid2>
                    {metadataAttributes.map((element, index) => {
                      const attribute = configCapabilitiesAttributes?.[capabilityName]?.attributes?.[index] || {};
                      const showMinFwVersion = Object.prototype.hasOwnProperty.call(element || {}, "minFwVersion");
                      const attributeKey = attribute.key || element?.key || "";
                      const minFwVersion = attribute.minFwVersion ?? (element?.minFwVersion != null ? String(element.minFwVersion) : "");
                      return (
                        <Fragment key={`${capabilityName}_${index}`}>
                          <Grid2 size={{ xs: 12, md: 4 }}>
                            <FormInput
                              type="text"
                              label="Attribute Key"
                              value={attributeKey}
                              disabled
                              fullWidth
                              size="small"
                              sx={selectInputSx}
                            />
                          </Grid2>
                          {showMinFwVersion && (
                            <Grid2 size={{ xs: 12, md: 4 }}>
                              <FormInput
                                type="number"
                                label="Min FW Version"
                                placeholder="e.g. 2.05"
                                value={minFwVersion}
                                onChange={(e) => onChangeMinFwVersion(capabilityName, index, e.target.value)}
                                fullWidth
                                size="small"
                                sx={selectInputSx}
                              />
                            </Grid2>
                          )}
                        </Fragment>
                      );
                    })}
                  </Grid2>
                )}
              </CardContent>
            </Card>
          </Fragment>
        );
      })}
    </Fragment>
  );
};

const mapStateToProps = (state) => ({
  ModelData: state.getModel,
});

export default connect(mapStateToProps)(DeviceModelConfigCapability);
